/**
 * V4.42.1 — Modal de secours plein écran pour le briefing stratégique.
 *
 * Affiché quand le presse-papiers natif échoue (iOS Safari, PWA non-sécurisée,
 * permissions refusées, etc.).
 *
 * Permet à l'utilisateur de :
 *   - Voir le briefing complet dans un textarea
 *   - Cliquer "Sélectionner tout" pour tout sélectionner
 *   - Cliquer "Copier" (tentative ultime via execCommand dans un gesture frais)
 *   - À défaut : long-press iOS → "Tout sélectionner" → "Copier"
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Copy, SelectionAll } from "@phosphor-icons/react";

export default function BriefingFallbackModal({ markdown, sizeKb, onClose }) {
  const taRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // Focus le textarea à l'ouverture pour permettre Cmd+A / long-press iOS
  useEffect(() => {
    if (taRef.current) {
      taRef.current.focus();
    }
    // Bloque le scroll du body pendant que la modal est ouverte
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleSelectAll = () => {
    if (!taRef.current) return;
    taRef.current.focus();
    taRef.current.select();
    taRef.current.setSelectionRange(0, markdown.length);
    toast.info("Texte sélectionné — utilise Cmd+C / Ctrl+C ou « Copier »");
  };

  const handleCopy = async () => {
    if (!taRef.current) return;
    taRef.current.focus();
    taRef.current.select();
    taRef.current.setSelectionRange(0, markdown.length);

    // Tentative 1 : Clipboard API (rafraîchie par le user gesture du bouton "Copier")
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(markdown);
        ok = true;
        console.info("[Briefing] Modal copy via clipboard.writeText OK");
      }
    } catch (err) {
      console.warn("[Briefing] Modal clipboard.writeText failed:", err);
    }

    // Tentative 2 : execCommand (legacy, fonctionne souvent sur iOS si le textarea est sélectionné)
    if (!ok) {
      try {
        ok = document.execCommand("copy");
        console.info("[Briefing] Modal copy via execCommand:", ok);
      } catch (err) {
        console.warn("[Briefing] Modal execCommand failed:", err);
      }
    }

    if (ok) {
      setCopied(true);
      toast.success(`Briefing copié — ${sizeKb} KB`, {
        description: "Colle-le dans ChatGPT pour ton plan d'action du jour.",
      });
      window.setTimeout(() => onClose?.(), 900);
    } else {
      toast.error("Copie automatique impossible — utilise « Tout sélectionner » puis copie manuelle (long-press iOS)");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Briefing stratégique — copie manuelle"
      data-testid="briefing-fallback-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="bg-white w-full h-full md:h-auto md:max-w-3xl md:max-h-[90vh] md:rounded-2xl flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div>
            <div className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">
              Briefing stratégique — copie manuelle
            </div>
            <div className="text-[13px] font-extrabold text-slate-900 mt-0.5">
              {sizeKb} KB · {markdown.length.toLocaleString("fr-FR")} caractères
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-1 rounded-lg text-slate-500 hover:bg-slate-200 transition"
            aria-label="Fermer"
            data-testid="briefing-fallback-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Instructions */}
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900 font-semibold">
          ℹ La copie automatique a échoué (iOS Safari ou permissions). Utilise <strong>Sélectionner tout</strong> puis copie manuelle, ou réessaye <strong>Copier</strong>.
        </div>

        {/* Textarea */}
        <div className="flex-1 overflow-hidden p-3">
          <textarea
            ref={taRef}
            readOnly
            value={markdown}
            className="w-full h-full min-h-[300px] resize-none font-mono text-[11px] leading-relaxed text-slate-800 bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
            onFocus={(e) => e.target.select()}
            data-testid="briefing-fallback-textarea"
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 px-4 py-3 border-t border-slate-200 bg-white">
          <button
            type="button"
            onClick={handleSelectAll}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-extrabold bg-white text-slate-900 border border-slate-300 hover:bg-slate-50 transition"
            data-testid="briefing-fallback-select-all"
          >
            <SelectionAll size={16} weight="bold" />
            Tout sélectionner
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-extrabold transition ${
              copied
                ? "bg-emerald-600 text-white"
                : "bg-slate-900 text-white hover:bg-slate-800"
            }`}
            data-testid="briefing-fallback-copy"
          >
            <Copy size={16} weight="bold" />
            {copied ? "Copié ✓" : "Copier"}
          </button>
        </div>
      </div>
    </div>
  );
}
